import React from 'react';

import { HStack, VStack } from '@betterment/samba-react';
import { useFeatureFlag } from '@betterment/shared-components';
import { ThemedImage } from '@betterment/shared-components/src/components/themed-image/ThemedImage';
import {
  Box,
  CONTENT_MAX_WIDTH,
  GoalIcon,
  Heading,
  SectionLayout,
  SiteLayout,
  TextProps,
  get as themeGet,
} from '@betterment/style-closet-react';
import styled from '@emotion/styled';
import { Helmet } from 'react-helmet';
import { NavLink } from 'react-router-dom';
import { FlexboxProps, ResponsiveValue, flexbox, space } from 'styled-system';

import { useAccount } from './AccountProvider';
import Balance from './Balance';
import { useCustomer } from './CustomerProvider';

import { trackClick } from '../../features/track';
import { urlBuilder } from '../../Shared/urlBuilder';
import { useUser } from '../contexts/UserProvider';

interface Props {
  title: string;
  children:
    | React.ReactNode
    | [React.ReactNode, React.ReactNode]
    | [React.ReactNode, React.ReactNode, React.ReactNode]
    | null;
}

const ActiveLink = styled(NavLink)<Omit<TextProps, 'color'>>`
  && {
    font-size: ${themeGet('fontSizes.2')};
    color: ${themeGet('colors.bmtGrey50')};
  }
  &&.active {
    border-bottom: 2px solid ${themeGet('colors.bmtGrey90')};
    color: ${themeGet('colors.bmtGrey90')};
  }
  &:hover,
  &:active {
    text-decoration: none;
    color: ${themeGet('colors.bmtGrey90')};
    outline-color: transparent;
  }
  &:focus {
    outline-color: 'blue';
  }
  ${space}
`;

const SiteLayoutStyled = styled(SiteLayout)`
  height: auto;
`;

const FlexItem = styled(Box)<FlexboxProps>`
  ${flexbox}
`;

interface SlotProps {
  order?: ResponsiveValue<number>;
}

export const Slot: React.FC<React.PropsWithChildren<SlotProps>> = ({ children }) => <>{children}</>;
export const Banner: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => <>{children}</>;

interface LayoutComponent<P = {}> extends React.FC<P> {
  Slot: React.FC<React.PropsWithChildren<SlotProps>>;
  Banner: React.FC<React.PropsWithChildren<unknown>>;
}

const CheckingLayout: LayoutComponent<Props> = ({ title, children }) => {
  const { account } = useAccount();
  const { user } = useUser();
  const { customer } = useCustomer();
  const { enabled: isEnvelopesExperienceEnabled } = useFeatureFlag({
    splitName: 'retail.envelopes_experience_q1_2025_enabled',
    context: 'CheckingSummary',
  });
  const icon = { light: account.icon.lightUrl, dark: account.icon.darkUrl };

  const hasMultipleBettermentAccounts = !user?.isCheckingOnly || customer.accounts.length > 1;
  const [item1, item2, item3] = React.Children.toArray(children) as React.ReactElement[];

  let [slot1, slot2]: React.ReactElement<SlotProps>[] = [];
  let banner: React.ReactElement | false = false;

  if (item1.type === Banner) {
    banner = item1;
    slot1 = item2 as React.ReactElement<SlotProps>;
    slot2 = item3 as React.ReactElement<SlotProps>;
  } else {
    slot1 = item1 as React.ReactElement<SlotProps>;
    slot2 = item2 as React.ReactElement<SlotProps>;
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <SiteLayoutStyled>
        <SiteLayout.Header mb={[6, 6, 0]}>
          {banner && banner}
          <SectionLayout bg="white" px="0">
            <HStack css={{ width: '100%', justifyContent: 'center', pt: { base: 'space.300', md: 'space.400' } }}>
              <Box width="100%">
                <Box px={[6, 6, 9]}>
                  <Box maxWidth={CONTENT_MAX_WIDTH} mx="auto" width="100%">
                    <VStack>
                      <HStack css={{ alignItems: 'center' }}>
                        <Box width={['4rem', '4rem', '4.8rem']} height={['4rem', '4rem', '4.8rem']}>
                          {isEnvelopesExperienceEnabled ? (
                            <ThemedImage
                              themedAsset={icon}
                              alt="checking"
                              css={{
                                width: { base: 'size.600' },
                                height: { base: 'size.600' },
                              }}
                            />
                          ) : (
                            <GoalIcon name="card" size={10} mr={3} />
                          )}
                        </Box>
                        <Heading variant="primary" pl={[2, 2, 2, 6]} mb="0">
                          {account.name}
                        </Heading>
                      </HStack>
                    </VStack>
                  </Box>
                </Box>
                <Box display={['block', 'block', 'none']}>
                  <Box borderBottom="1px solid" borderColor="bmtGrey20" bg="white" width="100%">
                    <Box px="6">
                      <HStack css={{ alignItems: 'baseline', justifyContent: 'flex-start', pt: 'space.400' }}>
                        <ActiveLink
                          fontSize="2"
                          pb="6"
                          mr="9"
                          to={urlBuilder.getAppCheckingSummary({ accountId: account.id })}
                          end={true}
                          onClick={() => trackClick({ name: 'CheckingOverview' })}
                        >
                          Overview
                        </ActiveLink>
                        <ActiveLink
                          fontSize="2"
                          pb="6"
                          to={urlBuilder.getAppCheckingAccountTransactions({ accountId: account.id })}
                          onClick={() => trackClick({ name: 'CheckingTransactions' })}
                        >
                          Transactions
                        </ActiveLink>
                      </HStack>
                    </Box>
                  </Box>
                  <Box px="6">
                    <Balance {...account} hasMultipleBettermentAccounts={hasMultipleBettermentAccounts} />
                  </Box>
                </Box>

                <Box display={['none', 'none', 'block']}>
                  <Box borderBottom="1px solid" borderColor="bmtGrey20" bg="white" width="100%">
                    <Box px="9">
                      <Box maxWidth={CONTENT_MAX_WIDTH} mx="auto" width="100%">
                        <Balance {...account} hasMultipleBettermentAccounts={hasMultipleBettermentAccounts} />
                      </Box>
                    </Box>
                  </Box>
                  <Box px="9">
                    <Box maxWidth={CONTENT_MAX_WIDTH} mx="auto" width="100%">
                      <HStack css={{ alignItems: 'baseline', justifyContent: 'flex-start', pt: 'space.200' }}>
                        <ActiveLink
                          fontSize="2"
                          pb="6"
                          mr="9"
                          to={urlBuilder.getAppCheckingSummary({ accountId: account.id })}
                          end={true}
                          onClick={() => trackClick({ name: 'CheckingOverview' })}
                        >
                          Overview
                        </ActiveLink>
                        <ActiveLink
                          fontSize="2"
                          pb="6"
                          to={urlBuilder.getAppCheckingAccountTransactions({ accountId: account.id })}
                          onClick={() => trackClick({ name: 'CheckingTransactions' })}
                        >
                          Transactions
                        </ActiveLink>
                      </HStack>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </HStack>
          </SectionLayout>
        </SiteLayout.Header>
        <SiteLayout.Main>
          <HStack css={{ justifyContent: 'center' }}>
            <Box
              maxWidth={CONTENT_MAX_WIDTH}
              mx={[0, 0, 9]}
              width="100%"
              borderTop={['1px solid', null, 'none']}
              borderColor="bmtGrey20"
            >
              <HStack css={{ flexWrap: { base: 'wrap', md: 'nowrap' } }}>
                <FlexItem order={slot1.props.order} flex="1 1 auto" mb={[3, 3, 0]}>
                  {slot1}
                </FlexItem>
                {slot2 && (
                  <FlexItem order={slot2.props.order} flex="0 0 33.33%" pl={[0, 0, 9]} pt={[0, 0, 9]}>
                    {slot2}
                  </FlexItem>
                )}
              </HStack>
            </Box>
          </HStack>
        </SiteLayout.Main>
      </SiteLayoutStyled>
    </>
  );
};

CheckingLayout.Slot = Slot;
CheckingLayout.Banner = Banner;

export default CheckingLayout;
